import React, { useEffect } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  Handshake as HandshakeIcon,
  Person as PersonIcon,
  Public as CountryIcon,
  Event as DateIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import MarketSelector from "../primitives/MarketSelector";

const ConfigDetails = ({
  formValues,
  onFormValueChange,
  filePreview,
  fileMetadata,
  ageRanges,
  selectedRange,
  onRangeSelect,
  onAgeChange,
  newCountryCode,
  onNewCountryCodeChange,
  onAddCountryRange,
}) => {
  // Auto-inference logic
  useEffect(() => {
    if (fileMetadata && filePreview) {
      // Auto-infer partner name from filename (without extension)
      if (!formValues.partnerName && fileMetadata.name) {
        const nameWithoutExtension = fileMetadata.name.replace(/\.(csv|json)$/i, "");
        // Clean up the name by replacing underscores and hyphens with spaces and capitalizing
        const cleanName = nameWithoutExtension
          .replace(/[_-]/g, " ")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
        onFormValueChange("partnerName", cleanName);
      }

      // Auto-infer country field
      if (!formValues.countryAttribute && filePreview) {
        const countryFields = Object.keys(filePreview).filter((key) => {
          const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, "");
          return /^(country|countries|location|region|nation|countrycode|countryname)$/.test(
            normalizedKey
          );
        });
        if (countryFields.length > 0) {
          onFormValueChange("countryAttribute", countryFields[0]);
        }
      }

      // Auto-infer date of birth field
      if (!formValues.dobAttribute && filePreview) {
        const dobFields = Object.keys(filePreview).filter((key) => {
          const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, "");
          return /^(dob|dateofbirth|birthdate|birthday|birth|dateborn|bdate)$/.test(normalizedKey);
        });
        if (dobFields.length > 0) {
          onFormValueChange("dobAttribute", dobFields[0]);
        }
      }
    }
  }, [fileMetadata, filePreview, formValues, onFormValueChange]);

  // Track which fields were auto-detected for UI feedback
  const autoDetectedFields = [];
  if (fileMetadata?.name && formValues.partnerName) {
    autoDetectedFields.push("partnerName");
  }
  if (filePreview && formValues.countryAttribute) {
    const countryFields = Object.keys(filePreview).filter((key) => {
      const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, "");
      return /^(country|countries|location|region|nation|countrycode|countryname)$/.test(
        normalizedKey
      );
    });
    if (countryFields.includes(formValues.countryAttribute)) {
      autoDetectedFields.push("country");
    }
  }
  if (filePreview && formValues.dobAttribute) {
    const dobFields = Object.keys(filePreview).filter((key) => {
      const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, "");
      return /^(dob|dateofbirth|birthdate|birthday|birth|dateborn|bdate)$/.test(normalizedKey);
    });
    if (dobFields.includes(formValues.dobAttribute)) {
      autoDetectedFields.push("dob");
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Configuration Details
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
        Set up the basic configuration parameters for your data processing pipeline.
      </Typography>

      <Grid container spacing={4}>
        {/* Partner Configuration Card */}
        <Grid size={12}>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardHeader
              avatar={<HandshakeIcon color="primary" />}
              title="Partner Configuration"
              subheader="Define your partner and consumer identification settings"
              titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid size={6}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      fullWidth
                      label="Partner Name"
                      placeholder="Enter partner name"
                      value={formValues.partnerName}
                      onChange={(e) => onFormValueChange("partnerName", e.target.value)}
                      InputProps={{
                        startAdornment: <HandshakeIcon sx={{ mr: 1, color: "action.active" }} />,
                      }}
                      helperText={
                        fileMetadata?.name && formValues.partnerName
                          ? "Auto-detected from filename (editable)"
                          : "This will be used as the partner identifier in the system"
                      }
                    />
                    {fileMetadata?.name && formValues.partnerName && (
                      <Chip
                        size="small"
                        label="Auto-detected"
                        color="success"
                        variant="outlined"
                        sx={{ alignSelf: "flex-start" }}
                      />
                    )}
                  </Box>
                </Grid>
                {/* <Grid size={6}>
                  <TextField
                    select
                    fullWidth
                    label="Consumer Identifier"
                    value={formValues.hashIdentifier}
                    onChange={(e) => onFormValueChange("hashIdentifier", e.target.value)}
                    InputProps={{
                      startAdornment: <SecurityIcon sx={{ mr: 1, color: "action.active" }} />,
                    }}
                    helperText="Primary field used to identify unique consumers"
                  >
                    <MenuItem value="email">Email Address</MenuItem>
                    <MenuItem value="phone">Phone Number</MenuItem>
                  </TextField>
                </Grid> */}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Data Mapping Card */}
        <Grid size={12}>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardHeader
              avatar={<PersonIcon color="primary" />}
              title="Data Field Mapping"
              subheader="Map your source data fields to standard consumer attributes"
              titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid size={6}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      select
                      fullWidth
                      label="Country Field"
                      value={formValues.countryAttribute || ""}
                      onChange={(e) => onFormValueChange("countryAttribute", e.target.value)}
                      InputProps={{
                        startAdornment: <CountryIcon sx={{ mr: 1, color: "action.active" }} />,
                      }}
                      helperText={
                        autoDetectedFields.includes("country")
                          ? "Auto-detected field (editable)"
                          : "Field containing country information"
                      }
                    >
                      <MenuItem value="">
                        <em>Select country field from your data</em>
                      </MenuItem>
                      {Object.keys(filePreview || {}).map((key) => (
                        <MenuItem key={key} value={key}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip size="small" label={key} variant="outlined" />
                            {filePreview[key] && (
                              <Typography variant="caption" color="textSecondary">
                                e.g., &ldquo;
                                {String(filePreview[key]).slice(0, 50)}
                                {String(filePreview[key]).length > 50 ? "..." : ""}
                                &rdquo;
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                    {autoDetectedFields.includes("country") && (
                      <Chip
                        size="small"
                        label="Auto-detected"
                        color="success"
                        variant="outlined"
                        sx={{ alignSelf: "flex-start" }}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      select
                      fullWidth
                      label="Date of Birth Field"
                      value={formValues.dobAttribute || ""}
                      onChange={(e) => onFormValueChange("dobAttribute", e.target.value)}
                      InputProps={{
                        startAdornment: <DateIcon sx={{ mr: 1, color: "action.active" }} />,
                      }}
                      helperText={
                        autoDetectedFields.includes("dob")
                          ? "Auto-detected field (editable)"
                          : "Field containing date of birth information"
                      }
                    >
                      <MenuItem value="">
                        <em>Select date of birth field from your data</em>
                      </MenuItem>
                      {Object.keys(filePreview || {}).map((key) => (
                        <MenuItem key={key} value={key}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip size="small" label={key} variant="outlined" />
                            {filePreview[key] && (
                              <Typography variant="caption" color="textSecondary">
                                e.g., &ldquo;
                                {String(filePreview[key]).slice(0, 50)}
                                {String(filePreview[key]).length > 50 ? "..." : ""}
                                &rdquo;
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                    {autoDetectedFields.includes("dob") && (
                      <Chip
                        size="small"
                        label="Auto-detected"
                        color="success"
                        variant="outlined"
                        sx={{ alignSelf: "flex-start" }}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Age Gate Configuration Card */}
        <Grid size={12}>
          <Card elevation={2}>
            <CardHeader
              avatar={<SecurityIcon color="primary" />}
              title="Age Gate Configuration"
              subheader="Set minimum and maximum age requirements by market"
              titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
            />
            <CardContent>
              <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>Age Verification</AlertTitle>
                Configure age requirements for different markets. Default settings apply to all
                markets unless overridden by specific country codes.
              </Alert>

              <MarketSelector
                title="Select Market"
                selectedMarket={selectedRange}
                onMarketSelect={onRangeSelect}
                markets={ageRanges}
                newMarketCode={newCountryCode}
                onNewMarketCodeChange={(e) => onNewCountryCodeChange(e.target.value.toUpperCase())}
                onAddMarket={onAddCountryRange}
                newMarketInputLabel="Country Code"
                newMarketInputPlaceholder="e.g., US, GB, DE, FR, AU"
                newMarketInputHelper="ISO country code (will be automatically uppercase)"
              />

              {selectedRange !== "add" && (
                <Paper variant="outlined" sx={{ p: 3, backgroundColor: "grey.50", mt: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Age Requirements for{" "}
                      <Chip
                        label={selectedRange === "default" ? "DEFAULT" : selectedRange}
                        size="small"
                        color={selectedRange === "default" ? "primary" : "secondary"}
                      />
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          label="Minimum Age"
                          type="number"
                          value={ageRanges[selectedRange]?.min || ""}
                          onChange={(e) => onAgeChange("min", e.target.value)}
                          inputProps={{ min: 1, max: 120 }}
                          helperText="Minimum age required for data collection"
                        />
                      </Grid>
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          label="Maximum Age"
                          type="number"
                          value={ageRanges[selectedRange]?.max || ""}
                          onChange={(e) => onAgeChange("max", e.target.value)}
                          inputProps={{ min: 1, max: 120 }}
                          helperText="Maximum age for data collection validity"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConfigDetails;
